import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
// import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import DatePickerInput from "../../forms/input/DatePickerInput";
import { fetchGETRequest } from "../../utils/services";
import { generateBillV2 } from "../../utils/billingServiceV2";

interface BillGenerateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BillGenerateModal: React.FC<BillGenerateModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [contracts, setContracts] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    customerId: "",
    contractId: "",
    siteId: "",
    billingPeriodStart: "",
    billingPeriodEnd: "",
    dueDate: "",
  });

  // Fetch customers on open
  useEffect(() => {
    if (open) {
      const fetchCustomers = async () => {
        try {
          const res: any = await fetchGETRequest("v2/Customers", {}, true);
          console.log("Customers fetch response:", res);
          if (res && res.data && res.data.data) {
            setCustomers(
              res.data.data.map((c: any) => ({
                label: `${c.name} (${c.customerCode || c.title || ""})`,
                value: c.id,
                refId: c.refId,
              })),
            );
          }
        } catch (err) {
          console.error("Error fetching customers", err);
        }
      };
      fetchCustomers();
    } else {
      setFormData({
        customerId: "",
        contractId: "",
        siteId: "",
        billingPeriodStart: "",
        billingPeriodEnd: "",
        dueDate: "",
      });
      setContracts([]);
      setSites([]);
    }
  }, [open]);

  // Fetch contracts when customer changes
  useEffect(() => {
    if (formData.customerId) {
      const fetchContracts = async () => {
        try {
          const res: any = await fetchGETRequest(
            `v2/Customers/${formData.customerId}/contracts`,
            {},
            true,
          );
          if (res && res.data && res.data.data) {
            setContracts(
              res.data.data.map((c: any) => ({
                label: c.contractNumber || c.id,
                value: c.id,
                refId: c.refId,
              })),
            );
          }
        } catch (err) {
          console.error("Error fetching contracts", err);
        }
      };
      fetchContracts();
    }
  }, [formData.customerId]);

  // Fetch sites when contract changes
  useEffect(() => {
    if (formData.contractId) {
      // Assuming sites are linked to contracts or just fetching sites for the customer
      const fetchSites = async () => {
        try {
          const res: any = await fetchGETRequest(
            `v2/Contracts/${formData.contractId}/sites`,
            {},
            true,
          );
          if (res && res.data && res.data.data) {
            setSites(
              res.data.data.map((s: any) => ({
                label: s.name || s.siteCode || s.id,
                value: s.id,
                refId: s.refId,
              })),
            );
          }
        } catch (err) {
          console.error("Error fetching sites", err);
        }
      };
      fetchSites();
    }
  }, [formData.contractId, formData.customerId]);

  const handleGenerate = async () => {
    if (
      !formData.customerId ||
      !formData.billingPeriodStart ||
      !formData.billingPeriodEnd
    ) {
      alert("Please fill in required fields (Customer and Dates)");
      return;
    }

    setLoading(true);
    try {
      const selectedCustomer = customers.find(
        (c) => c.value === formData.customerId,
      );
      const selectedContract = contracts.find(
        (c) => c.value === formData.contractId,
      );
      const selectedSite = sites.find((s) => s.value === formData.siteId);

      const payload = {
        ...formData,
        customerId: selectedCustomer?.refId || formData.customerId,
        contractId: selectedContract?.refId || formData.contractId,
        siteId: selectedSite?.refId || formData.siteId,
      };

      const res = await generateBillV2(payload);
      if (res && res.success) {
        onSuccess();
      } else {
        alert("Failed to generate bill: " + (res.errors || "Unknown error"));
      }
    } catch (err) {
      console.error("Error generating bill", err);
      alert("Error generating bill");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">Generate New Bill</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <CustomOutlinedSelect
            label="Customer"
            required
            options={customers}
            value={formData.customerId}
            onChange={(opt) =>
              setFormData({ ...formData, customerId: opt.value as string })
            }
            placeholder="Select Customer"
          />
          <CustomOutlinedSelect
            label="Contract"
            options={contracts}
            value={formData.contractId}
            onChange={(opt) =>
              setFormData({ ...formData, contractId: opt.value as string })
            }
            placeholder="Select Contract"
            isDisabled={!formData.customerId}
          />
          <CustomOutlinedSelect
            label="Site"
            options={sites}
            value={formData.siteId}
            onChange={(opt) =>
              setFormData({ ...formData, siteId: opt.value as string })
            }
            placeholder="Select Site"
            isDisabled={!formData.contractId}
          />
          <Stack direction="row" spacing={2}>
            <DatePickerInput
              label="Period Start"
              required
              // showTimeSelect={false}
              value={formData.billingPeriodStart}
              onChange={(date) =>
                setFormData({
                  ...formData,
                  billingPeriodStart: date
                    ? new Date(
                        date.getTime() - date.getTimezoneOffset() * 60000,
                      ).toISOString()
                    : "",
                })
              }
            />
            <DatePickerInput
              label="Period End"
              required
              // showTimeSelect={false}
              value={formData.billingPeriodEnd}
              onChange={(date) =>
                setFormData({
                  ...formData,
                  billingPeriodEnd: date
                    ? new Date(
                        date.getTime() - date.getTimezoneOffset() * 60000,
                      ).toISOString()
                    : "",
                })
              }
            />
          </Stack>
          <DatePickerInput
            label="Due Date"
            // showTimeSelect={false}
            value={formData.dueDate}
            onChange={(date) =>
              setFormData({
                ...formData,
                dueDate: date
                  ? new Date(
                      date.getTime() - date.getTimezoneOffset() * 60000,
                    ).toISOString()
                  : "",
              })
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ textTransform: "none", borderRadius: "8px", px: 4 }}
        >
          {loading ? "Generating..." : "Generate Bill"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillGenerateModal;
